import React, { useMemo, useState } from 'react';
import { cn } from './cn';
import { Icon } from './base/Icon';

/**
 * DateRangePicker — ช่วงวันที่แบบไทย ปีพุทธศักราช
 *
 * กติกาเดิมจาก handoff: แสดงวันที่เป็น พ.ศ. ทุกจุด (README ข้อ 3)
 * ค่าที่ส่งออกยังเป็น ISO (ค.ศ.) เพื่อให้ API ใช้ได้ตรง — แปลงเฉพาะตอนแสดง
 */
export interface DateRange {
  /** ISO date 'YYYY-MM-DD' (ค.ศ.) */
  from: string | null;
  to: string | null;
}

export interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  label?: string;
  /** ปุ่มช่วงที่ใช้บ่อย */
  presets?: Array<{ id: string; label: string; resolve: () => DateRange }>;
  className?: string;
}

const iso = (d: Date) => d.toISOString().slice(0, 10);

/** แสดงวันที่เป็น พ.ศ. — 2026-08-21 → 21 ส.ค. 2569 */
export function formatThaiDate(value: string | null): string {
  if (!value) return '—';
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '—';
  const months = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear() + 543}`;
}

export const DEFAULT_PRESETS: NonNullable<DateRangePickerProps['presets']> = [
  { id: 'today', label: 'วันนี้', resolve: () => { const t = iso(new Date()); return { from: t, to: t }; } },
  {
    id: '7d',
    label: '7 วันที่ผ่านมา',
    resolve: () => {
      const to = new Date();
      const from = new Date();
      from.setDate(from.getDate() - 6);
      return { from: iso(from), to: iso(to) };
    },
  },
  {
    id: 'month',
    label: 'เดือนนี้',
    resolve: () => {
      const now = new Date();
      return { from: iso(new Date(now.getFullYear(), now.getMonth(), 1)), to: iso(now) };
    },
  },
  {
    id: 'point-year',
    label: 'รอบปีคะแนน',
    resolve: () => {
      // รอบรีเซ็ตคะแนนรายปี ตั้งค่าที่หน้ากติกาคะแนน (FR-ADM-24) — ค่าเริ่มต้นคือปีปฏิทิน
      const now = new Date();
      return { from: iso(new Date(now.getFullYear(), 0, 1)), to: iso(now) };
    },
  },
];

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  value, onChange, label = 'ช่วงวันที่', presets = DEFAULT_PRESETS, className,
}) => {
  const [open, setOpen] = useState(false);
  const summary = useMemo(() => {
    if (!value.from && !value.to) return 'ทุกช่วงเวลา';
    if (value.from && value.to && value.from === value.to) return formatThaiDate(value.from);
    return `${formatThaiDate(value.from)} – ${formatThaiDate(value.to)}`;
  }, [value]);

  return (
    <div className={cn('relative font-[family-name:var(--font-thai)]', className)}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex min-h-[var(--hit-control)] items-center gap-[var(--space-15)] rounded-[var(--radius-sm)] border border-[var(--line-300)] bg-[var(--surface-0)] px-[var(--space-3)] text-[length:var(--text-form)] text-[var(--ink-900)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
      >
        <Icon name="date_range" size={16} className="text-[var(--ink-600)]" />
        <span className="text-[var(--ink-600)]">{label}:</span>
        <span className="font-[var(--weight-medium)]">{summary}</span>
        <Icon name={open ? 'expand_less' : 'expand_more'} size={16} className="text-[var(--ink-600)]" />
      </button>

      {open && (
        <div
          className="absolute left-0 z-[var(--z-dropdown)] mt-[var(--space-1)] w-[320px] rounded-[var(--radius-md)] bg-[var(--surface-0)] p-[var(--space-3)] shadow-[var(--elevation-dropdown)]"
          onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOpen(false); }}
        >
          <div className="mb-[var(--space-3)] flex flex-wrap gap-[var(--space-1)]">
            {presets.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { onChange(p.resolve()); setOpen(false); }}
                className="min-h-[var(--hit-table)] rounded-[var(--radius-pill)] border border-[var(--line-300)] px-[var(--space-25)] text-[length:var(--text-table-sm)] text-[var(--ink-700)] hover:bg-[var(--blue-050)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
              >
                {p.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-2 gap-[var(--space-2)]">
            <label className="flex flex-col gap-[var(--space-1)] text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
              ตั้งแต่
              <input
                type="date"
                value={value.from ?? ''}
                onChange={(e) => onChange({ ...value, from: e.target.value || null })}
                className="min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-2)] font-[family-name:var(--font-num)] text-[length:var(--text-form)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
              />
              <span className="text-[var(--ink-600)]">{formatThaiDate(value.from)}</span>
            </label>
            <label className="flex flex-col gap-[var(--space-1)] text-[length:var(--text-table-sm)] text-[var(--ink-700)]">
              ถึง
              <input
                type="date"
                value={value.to ?? ''}
                onChange={(e) => onChange({ ...value, to: e.target.value || null })}
                className="min-h-[var(--hit-control)] rounded-[var(--radius-sm)] border border-[var(--line-300)] px-[var(--space-2)] font-[family-name:var(--font-num)] text-[length:var(--text-form)] outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
              />
              <span className="text-[var(--ink-600)]">{formatThaiDate(value.to)}</span>
            </label>
          </div>

          <div className="mt-[var(--space-3)] flex justify-between border-t border-[var(--line-200)] pt-[var(--space-2)]">
            <button
              type="button"
              onClick={() => { onChange({ from: null, to: null }); setOpen(false); }}
              className="min-h-[var(--hit-table)] rounded-[var(--radius-sm)] px-[var(--space-2)] text-[length:var(--text-form)] text-[var(--ink-700)] hover:underline outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)]"
            >
              ล้างช่วงวันที่
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="min-h-[var(--hit-table)] rounded-[var(--radius-sm)] bg-[var(--blue-700)] px-[var(--space-3)] text-[length:var(--text-form)] font-[var(--weight-medium)] text-white outline-none focus-visible:ring-[3px] focus-visible:ring-[var(--focus-ring-color)] focus-visible:ring-offset-2"
            >
              ใช้ช่วงนี้
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
